import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import { createPortal } from "react-dom";

import { isEditableTarget } from "../../lib/m4";
import {
  DOUBLE_CLICK_ZOOM,
  DRAG_SLOP,
  MAGNIFIER_ZOOM,
  PRESS_MS,
  PRESS_SLOP,
  STAGE_PADDING,
  clampPan,
  containRect,
  contentBox,
  formatCounter,
  formatMagnification,
  formatZoom,
  isPanorama,
  isPointOnImage,
  magnifierDiameter,
  magnifierPlacement,
  magnifierSource,
  magnifierZoomFactor,
  nextIndex,
  panBy,
  pinchZoom,
  resetZoom,
  viewerKey,
  wheelZoomFactor,
  zoomAt,
  type ImageViewerItem,
  type Point,
  type Rect,
  type Size,
  type ZoomState,
} from "../../lib/imageViewer";
import { PanoViewer } from "./PanoViewer";

export interface ImageViewerApi {
  /** 打开浮层：`items` 至少一张；`index` 越界会绕回首张。 */
  open: (items: readonly ImageViewerItem[], index?: number) => void;
  close: () => void;
  isOpen: boolean;
}

const ImageViewerContext = createContext<ImageViewerApi | null>(null);

/**
 * 任何组件都能调 `useImageViewer().open([{ src, title, subtitle }], index)`。
 * Provider 在 `App` 顶层，浮层本身 portal 到 body，不受业务容器的 overflow / transform 影响。
 */
export function useImageViewer(): ImageViewerApi {
  const api = useContext(ImageViewerContext);
  if (!api) throw new Error("useImageViewer 必须放在 <ImageViewerProvider> 里");
  return api;
}

interface OpenRequest {
  items: ImageViewerItem[];
  index: number;
  session: number;
}

export function ImageViewerProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<OpenRequest | null>(null);

  const open = useCallback((items: readonly ImageViewerItem[], index = 0) => {
    if (items.length === 0) return;
    setRequest((prev) => ({
      items: [...items],
      index: nextIndex(0, index, items.length),
      // 每次 open 都换 key：复用组件会带着上一张的缩放 / 放大镜状态。
      session: (prev?.session ?? 0) + 1,
    }));
  }, []);

  const close = useCallback(() => setRequest(null), []);
  const api = useMemo<ImageViewerApi>(
    () => ({ open, close, isOpen: request !== null }),
    [open, close, request],
  );

  return (
    <ImageViewerContext.Provider value={api}>
      {children}
      {request ? (
        <ImageViewer
          key={request.session}
          items={request.items}
          initialIndex={request.index}
          onClose={close}
        />
      ) : null}
    </ImageViewerContext.Provider>
  );
}

interface Press {
  x: number;
  y: number;
  id: number;
  timer: number | null;
  on: boolean;
}

interface Gesture {
  distance: number;
  centroid: Point;
  base: ZoomState;
}

const ZERO_POINT: Point = { x: 0, y: 0 };
const ZERO_SIZE: Size = { width: 0, height: 0 };
const NO_RECT: Rect = { left: 0, top: 0, width: 0, height: 0 };

function toRect(rect: DOMRect | null): Rect {
  if (!rect) return NO_RECT;
  return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
}

function toSize(rect: Rect): Size {
  return { width: rect.width, height: rect.height };
}

/** 读一个 CSS 令牌；canvas 里画不了 `var()`，只能取值（取不到就用兜底色，比如 jsdom）。 */
function token(name: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

export interface ImageViewerProps {
  items: readonly ImageViewerItem[];
  initialIndex?: number;
  onClose: () => void;
}

/**
 * 全屏看图浮层。
 *
 * 交互照 `imgview.js`：
 *   滚轮 / 双指缩放 1–8 倍、双击放大/还原、拖动平移；
 *   长按 320ms 出圆形放大镜（移动超过 8px 算拖动，不出镜），镜跟指针、默认在上方、贴顶翻到下方，
 *   按住时滚轮调放大倍数 1.5–8×；放大镜 canvas 直接从原图取像素，缩放 / 平移后也对得上；
 *   键盘 0 还原 · + / - 缩放 · ← → 切换 · Esc 关闭（先收放大镜）；
 *   点图外的空白关闭；单张时不出左右切换；
 *   打开时记住 `document.activeElement`，关闭后还回去，浮层 `aria-modal`。
 *
 * 全景（宽高比 > 2 或 `panorama: true`）走 `PanoViewer`：整图按容器高度铺满、横向拖动环视。
 */
export function ImageViewer({ items, initialIndex = 0, onClose }: ImageViewerProps) {
  const [index, setIndex] = useState(() => nextIndex(0, initialIndex, items.length));
  const [zoom, setZoom] = useState<ZoomState>(resetZoom);
  const [natural, setNatural] = useState<Size | null>(null);
  const [magnifier, setMagnifier] = useState<Point | null>(null);
  const [magnification, setMagnification] = useState(MAGNIFIER_ZOOM);
  const [dragging, setDragging] = useState(false);
  const [failed, setFailed] = useState(false);

  const stageRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const pressRef = useRef<Press | null>(null);
  const gestureRef = useRef<Gesture | null>(null);
  const pointersRef = useRef(new Map<number, Point>());
  const movedRef = useRef(false);
  const suppressDblRef = useRef(0);
  const magnifierRef = useRef<Point | null>(null);
  const naturalRef = useRef<Size | null>(null);
  const panoRef = useRef(false);

  const item = items[index];
  const pano = item ? isPanorama(item, natural) : false;
  const diameter = typeof window === "undefined" ? 200 : magnifierDiameter(window.innerWidth, window.innerHeight);

  useEffect(() => {
    naturalRef.current = natural;
  }, [natural]);

  useEffect(() => {
    panoRef.current = pano;
  }, [pano]);

  // 打开时接管焦点，关闭后还回触发元素（imgview.js 的 `back`）；同时锁住背景滚动。
  useEffect(() => {
    const active = document.activeElement;
    restoreRef.current = active instanceof HTMLElement ? active : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const timer = window.setTimeout(() => closeRef.current?.focus({ preventScroll: true }), 0);
    return () => {
      window.clearTimeout(timer);
      document.body.style.overflow = previousOverflow;
      restoreRef.current?.focus?.({ preventScroll: true });
      restoreRef.current = null;
    };
  }, []);

  // 卸载时清掉还可能挂着的长按定时器。
  useEffect(() => {
    return () => {
      const press = pressRef.current;
      if (press?.timer != null) window.clearTimeout(press.timer);
      pressRef.current = null;
    };
  }, []);

  const stageRectNow = useCallback(
    () => toRect(stageRef.current?.getBoundingClientRect() ?? null),
    [],
  );
  const sizeNow = useCallback(() => toSize(stageRectNow()), [stageRectNow]);
  const contentNow = useCallback(
    () => contentBox(naturalRef.current ?? ZERO_SIZE, sizeNow(), panoRef.current),
    [sizeNow],
  );
  const paddingNow = useCallback(() => (panoRef.current ? 0 : STAGE_PADDING), []);

  /** 真正显示出来的图的矩形（缩放过、letterbox 已去掉）。 */
  const displayNow = useCallback((): Rect | null => {
    const img = imgRef.current;
    if (!img) return null;
    const box = toRect(img.getBoundingClientRect());
    const size = { width: img.naturalWidth, height: img.naturalHeight };
    if (!size.width || !size.height) return null;
    const display = containRect(box, size);
    return display.width > 0 && display.height > 0 ? display : null;
  }, []);

  function cancelPress() {
    const press = pressRef.current;
    if (press?.timer != null) window.clearTimeout(press.timer);
    if (press) press.timer = null;
  }

  function hideMagnifier() {
    magnifierRef.current = null;
    setMagnifier(null);
  }

  function resetView() {
    cancelPress();
    pressRef.current = null;
    hideMagnifier();
    setMagnification(MAGNIFIER_ZOOM);
    setZoom(resetZoom());
  }

  function go(delta: number) {
    if (items.length < 2) return;
    resetView();
    setNatural(null);
    naturalRef.current = null;
    setFailed(false);
    setIndex((prev) => nextIndex(prev, delta, items.length));
  }

  // ---------------------------------------------------------------- 手势
  function armPress(event: ReactPointerEvent<HTMLDivElement>) {
    cancelPress();
    const press: Press = {
      x: event.clientX,
      y: event.clientY,
      id: event.pointerId,
      timer: null,
      on: false,
    };
    press.timer = window.setTimeout(() => {
      press.timer = null;
      const img = imgRef.current;
      const display = displayNow();
      // 只在乎「指针下面真的有图」：留白 / letterbox 上按再久也不出镜。
      if (!img || !img.naturalWidth || !display || !isPointOnImage(press, display)) {
        if (pressRef.current === press) pressRef.current = null;
        return;
      }
      press.on = true;
      // 出镜之后抬手不再算「点空白关闭」；也压掉紧随其后的 dblclick。
      movedRef.current = true;
      suppressDblRef.current = performance.now() + 500;
      magnifierRef.current = { x: press.x, y: press.y };
      setMagnifier({ x: press.x, y: press.y });
      setMagnification(MAGNIFIER_ZOOM);
    }, PRESS_MS);
    pressRef.current = press;
  }

  function gestureStart() {
    const points = [...pointersRef.current.values()];
    if (points.length === 0) {
      gestureRef.current = null;
      return;
    }
    const sum = points.reduce((acc, point) => ({ x: acc.x + point.x, y: acc.y + point.y }), ZERO_POINT);
    const centroid = { x: sum.x / points.length, y: sum.y / points.length };
    const first = points[0] ?? ZERO_POINT;
    const second = points[1] ?? ZERO_POINT;
    gestureRef.current = {
      distance: points.length > 1 ? Math.hypot(first.x - second.x, first.y - second.y) : 0,
      centroid,
      base: zoom,
    };
  }

  function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    const stage = event.currentTarget;
    stage.setPointerCapture?.(event.pointerId);
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointersRef.current.size === 1) {
      movedRef.current = false;
      armPress(event);
    } else {
      // 第二根手指 / 第二个键：退出放大镜，照常双指缩放。
      cancelPress();
      pressRef.current = null;
      hideMagnifier();
    }
    gestureStart();
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    const press = pressRef.current;
    if (press && press.id === event.pointerId) {
      if (press.on) {
        // 已经出镜：指针只移动放大镜，不拖图。
        press.x = event.clientX;
        press.y = event.clientY;
        pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
        magnifierRef.current = { x: event.clientX, y: event.clientY };
        setMagnifier({ x: event.clientX, y: event.clientY });
        return;
      }
      if (
        press.timer !== null &&
        Math.hypot(event.clientX - press.x, event.clientY - press.y) > PRESS_SLOP
      ) {
        // 按住了但手在动：当拖动，不出放大镜。
        window.clearTimeout(press.timer);
        press.timer = null;
      }
    }

    const gesture = gestureRef.current;
    if (!pointersRef.current.has(event.pointerId) || !gesture) return;
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = [...pointersRef.current.values()];
    const sum = points.reduce((acc, point) => ({ x: acc.x + point.x, y: acc.y + point.y }), ZERO_POINT);
    const centroid = { x: sum.x / points.length, y: sum.y / points.length };
    if (Math.hypot(centroid.x - gesture.centroid.x, centroid.y - gesture.centroid.y) > DRAG_SLOP) {
      movedRef.current = true;
    }

    const first = points[0] ?? ZERO_POINT;
    const second = points[1] ?? ZERO_POINT;
    if (points.length > 1 && gesture.distance > 0) {
      movedRef.current = true;
      const distance = Math.hypot(first.x - second.x, first.y - second.y);
      setZoom(
        pinchZoom(
          gesture.base,
          gesture,
          { distance, centroid },
          stageRectNow(),
          contentNow(),
          paddingNow(),
        ),
      );
      setDragging(true);
      return;
    }
    // 1 倍时拖不动普通图（imgview.js 只在放大后平移）；全景 1 倍就要能横向环视。
    if ((gesture.base.s > 1 || panoRef.current) && movedRef.current) {
      setZoom(
        panBy(
          gesture.base,
          centroid.x - gesture.centroid.x,
          centroid.y - gesture.centroid.y,
          contentNow(),
          sizeNow(),
          paddingNow(),
        ),
      );
      setDragging(true);
    }
  }

  function handlePointerUp(event: ReactPointerEvent<HTMLDivElement>) {
    if (!pointersRef.current.has(event.pointerId)) return;
    pointersRef.current.delete(event.pointerId);
    const press = pressRef.current;
    if (press && press.id === event.pointerId) {
      cancelPress();
      hideMagnifier();
      pressRef.current = null;
    }
    setDragging(false);
    if (pointersRef.current.size > 0) {
      gestureStart();
      return;
    }
    gestureRef.current = null;
    if (!movedRef.current && event.type === "pointerup") {
      const display = displayNow();
      if (!display || !isPointOnImage({ x: event.clientX, y: event.clientY }, display)) onClose();
    }
  }

  function handleDoubleClick(event: ReactMouseEvent<HTMLDivElement>) {
    if (performance.now() < suppressDblRef.current) return;
    const display = displayNow();
    const point = { x: event.clientX, y: event.clientY };
    if (!display || !isPointOnImage(point, display)) return;
    const rect = stageRectNow();
    const content = contentNow();
    setZoom((prev) =>
      prev.s > 1
        ? resetZoom()
        : zoomAt(prev, DOUBLE_CLICK_ZOOM, point, rect, content, paddingNow()),
    );
  }

  // 滚轮必须非 passive 才能 preventDefault：用原生监听，React 的 onWheel 是 passive 的。
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    function handleWheel(event: WheelEvent) {
      event.preventDefault();
      if (magnifierRef.current) {
        setMagnification((prev) => magnifierZoomFactor(prev, event.deltaY));
        return;
      }
      const rect = toRect(stage?.getBoundingClientRect() ?? null);
      const content = contentBox(
        naturalRef.current ?? ZERO_SIZE,
        toSize(rect),
        panoRef.current,
      );
      setZoom((prev) =>
        zoomAt(
          prev,
          prev.s * wheelZoomFactor(event.deltaY, event.ctrlKey),
          { x: event.clientX, y: event.clientY },
          rect,
          content,
          panoRef.current ? 0 : STAGE_PADDING,
        ),
      );
    }
    stage.addEventListener("wheel", handleWheel, { passive: false });
    return () => stage.removeEventListener("wheel", handleWheel);
  }, []);

  // 窗口尺寸变了：重新夹一次平移，别把图留在视野外。
  useEffect(() => {
    function handleResize() {
      setZoom((prev) => clampPan(prev, contentNow(), sizeNow(), paddingNow()));
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [contentNow, sizeNow, paddingNow]);

  // 键盘：document 的捕获阶段接管，Esc 不会同时关掉下面的业务弹层。
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      const command = viewerKey(event.key, {
        modifier: event.metaKey || event.ctrlKey || event.altKey,
        editing: isEditableTarget(event.target),
      });
      if (!command) return;
      event.preventDefault();
      event.stopPropagation();
      if (command === "close") {
        if (magnifierRef.current) hideMagnifier();
        else onClose();
        return;
      }
      if (command === "prev" || command === "next") {
        if (items.length > 1) go(command === "next" ? 1 : -1);
        return;
      }
      if (command === "reset") {
        hideMagnifier();
        setMagnification(MAGNIFIER_ZOOM);
        setZoom(resetZoom());
        return;
      }
      const rect = stageRectNow();
      const content = contentNow();
      const center = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
      const factor = command === "zoom-in" ? 1.25 : 1 / 1.25;
      setZoom((prev) => zoomAt(prev, prev.s * factor, center, rect, content, paddingNow()));
    }
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  });

  // 放大镜：canvas 从原图画（不是放大 img），缩放 / 平移之后仍然对得上。
  useEffect(() => {
    if (!magnifier) return;
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const pixels = Math.max(1, Math.round(diameter * dpr));
    if (canvas.width !== pixels) canvas.width = pixels;
    if (canvas.height !== pixels) canvas.height = pixels;
    const context = canvas.getContext("2d");
    if (!context) return;
    const size = { width: img.naturalWidth, height: img.naturalHeight };
    if (!size.width || !size.height) return;
    const display = containRect(toRect(img.getBoundingClientRect()), size);
    const source = magnifierSource(magnifier, diameter, magnification, display, size);
    if (!source) return;
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    context.clearRect(0, 0, diameter, diameter);
    context.fillStyle = token("--thumb-bg", "#0a0a09");
    context.fillRect(0, 0, diameter, diameter);
    context.drawImage(
      img,
      source.clip.x,
      source.clip.y,
      source.clip.width,
      source.clip.height,
      source.draw.dx,
      source.draw.dy,
      source.draw.dw,
      source.draw.dh,
    );
  }, [magnifier, magnification, diameter]);

  function handleLoad(event: SyntheticEvent<HTMLImageElement>) {
    const img = event.currentTarget;
    const size = { width: img.naturalWidth, height: img.naturalHeight };
    naturalRef.current = size;
    setNatural(size);
    setFailed(false);
    // imgview.js：每次换图 / 加载完都回到 1 倍居中。
    setZoom(resetZoom());
  }

  if (!item) return null;

  const placement = magnifier
    ? magnifierPlacement(
        magnifier,
        diameter,
        { width: window.innerWidth, height: window.innerHeight },
      )
    : null;
  const multiple = items.length > 1;
  const label = item.title ?? "看图";
  const hint = pano
    ? "拖动环视 · 长按放大镜 · 滚轮缩放 · 0 还原 · Esc 关闭"
    : `长按放大镜 · 滚轮/双指缩放 · 双击放大/还原 · 拖动平移 · 0 还原${multiple ? " · ← → 切换 · Esc 关闭" : " · Esc 关闭"}`;

  return createPortal(
    <div
      className={`dp-iv${pano ? " is-pano" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label={label}
    >
      <div
        ref={stageRef}
        className={[
          "dp-iv-stage",
          zoom.s > 1 ? "is-zoomed" : "",
          dragging ? "is-dragging" : "",
          magnifier ? "is-lens" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onDoubleClick={handleDoubleClick}
        onContextMenu={(event) => {
          const press = pressRef.current;
          // 触屏长按不要弹系统菜单。
          if (press?.on || press?.timer != null) event.preventDefault();
        }}
      >
        {pano ? (
          <PanoViewer
            ref={imgRef}
            src={item.src}
            alt={item.title ?? "大图"}
            zoom={zoom}
            onLoad={handleLoad}
            onError={() => setFailed(true)}
          />
        ) : (
          <div
            className="dp-iv-frame"
            style={{ transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.s})` }}
          >
            <img
              ref={imgRef}
              src={item.src}
              alt={item.title ?? "大图"}
              draggable={false}
              onDragStart={(event) => event.preventDefault()}
              onLoad={handleLoad}
              onError={() => setFailed(true)}
            />
          </div>
        )}

        {pano ? <span className="dp-iv-badge">全景 · 拖动环视</span> : null}
        {failed ? <p className="dp-iv-error">图片加载失败</p> : null}
      </div>

      {multiple ? (
        <>
          <button
            type="button"
            className="dp-iv-nav is-prev"
            aria-label="上一张（←）"
            onClick={() => go(-1)}
          >
            ‹
          </button>
          <button
            type="button"
            className="dp-iv-nav is-next"
            aria-label="下一张（→）"
            onClick={() => go(1)}
          >
            ›
          </button>
        </>
      ) : null}

      <button
        ref={closeRef}
        type="button"
        className="dp-iv-close"
        aria-label="关闭看图"
        onClick={onClose}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>

      <div className="dp-iv-bar">
        <span className="dp-iv-title">
          {label}
          {formatCounter(index, items.length) ? (
            <em className="dp-iv-counter">{formatCounter(index, items.length)}</em>
          ) : null}
        </span>
        {item.subtitle ? <code className="dp-iv-sub">{item.subtitle}</code> : null}
        <span className="dp-iv-spacer" />
        <span className="dp-iv-zoom">{formatZoom(zoom.s)}</span>
        <span className="dp-iv-hint">{hint}</span>
        <button type="button" className="dp-btn sm" onClick={onClose}>
          关闭
        </button>
      </div>

      {magnifier && placement ? (
        <div
          className="dp-iv-loupe"
          data-flipped={placement.flipped ? "true" : undefined}
          style={{
            left: placement.left,
            top: placement.top,
            width: diameter,
            height: diameter,
          }}
          aria-hidden="true"
        >
          <canvas ref={canvasRef} />
          <span className="dp-iv-loupe-zoom">{formatMagnification(magnification)}</span>
        </div>
      ) : null}
    </div>,
    document.body,
  );
}
